import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import Swal from "sweetalert2";

const IssuedCertificates = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all, merit, participation

  const load = async () => {
    try {
      setLoading(true);
      const res = await API.fetchIssuedCertificates();
      setCerts(res?.data || res || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.message || "Failed to load certificates", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRevoke = async (id) => {
    const { value: reason } = await Swal.fire({
      title: "Revoke certificate?",
      input: "text",
      inputPlaceholder: "Reason (optional)",
      showCancelButton: true,
      confirmButtonText: "Revoke",
      confirmButtonColor: "#dc2626",
    });
    if (reason === undefined) return;
    try {
      await API.revokeCertificate(id, reason || "");
      await load();
      Swal.fire("Done", "Certificate revoked", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to revoke", "error");
    }
  };

  // Filter certificates
  let filtered = certs;
  if (filter === "merit") {
    filtered = certs.filter(
      (c) => c.medal_type && c.medal_type !== "Participation"
    );
  } else if (filter === "participation") {
    filtered = certs.filter((c) => c.medal_type === "Participation");
  }
  const meritCount = certs.filter(
    (c) => c.medal_type && c.medal_type !== "Participation"
  ).length;
  const participationCount = certs.filter(
    (c) => c.medal_type === "Participation"
  ).length;

  const getMedalEmoji = (medalType) => {
    if (medalType === "Gold") return "🥇";
    if (medalType === "Silver") return "🥈";
    if (medalType === "Bronze") return "🥉";
    return "📜";
  };

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Certificate Desk</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Issued Certificates</h1>
              <p className="text-sm text-slate-600 mt-1">
                Monitor issued records and revoke invalid certificates.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Total</p>
                <p className="text-lg font-semibold text-slate-900">{certs.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Merit</p>
                <p className="text-lg font-semibold text-slate-900">{meritCount}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Participation</p>
                <p className="text-lg font-semibold text-slate-900">{participationCount}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "all"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            All ({certs.length})
          </button>
          <button
            onClick={() => setFilter("merit")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "merit"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Merit Certificates ({meritCount})
          </button>
          <button
            onClick={() => setFilter("participation")}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
              filter === "participation"
                ? "bg-teal-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Participation Certificates ({participationCount})
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-800">
              {filter === "merit"
                ? "Merit Certificates"
                : filter === "participation"
                ? "Participation Certificates"
                : "All Certificates"}
            </h2>
            {loading && (
              <span className="text-xs text-gray-500 animate-pulse">
                Loading...
              </span>
            )}
          </div>

          {!filtered.length && !loading ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg">No certificates found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[1050px] w-full text-xs sm:text-sm divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Medal
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Player Name
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Event / Game
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Serial No
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Issued At
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filtered.map((c) => (
                    <tr
                      key={c.id}
                      className="hover:bg-gray-50 transition duration-150"
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-base">
                          {getMedalEmoji(c.medal_type)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-gray-900">
                          {c.player_name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700">
                        <div className="font-medium">{c.event_title}</div>
                        <div className="text-xs text-gray-500">
                          {c.game_name || c.sport_name}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-gray-600">
                        {c.serial_no}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {c.issued_at
                          ? new Date(c.issued_at).toLocaleString("en-IN")
                          : "-"}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {c.revoked ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold">
                            Revoked
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {!c.revoked && (
                          <button
                            onClick={() => handleRevoke(c.id)}
                            className="px-2.5 py-1 bg-red-600 text-white text-[11px] font-semibold rounded-lg hover:bg-red-700 transition shadow-sm"
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default IssuedCertificates;
